<template>
  <LmDialog v-model="modalShow" :styles="dialogStyles" :title="editMode ? 'Edit Field' : 'Create Field'">
    <template #toolbar-actions>
      <q-btn :disable="hasEmptyOptions" label="Save" color="tertiary" size="sm" @click="submit" />
    </template>

    <template #content>
      <q-card-section>
        <form v-if="formData" @submit.prevent="submit()">
          <div class="row q-col-gutter-md q-mb-md">
            <div class="col-xs-6">
              <enhanced-q-select
                id="entity"
                v-model="$v.formData.entity_id.$model"
                :error="$v.formData.entity_id.$error"
                :error-message="$v_displayMessage($v.formData.entity_id)"
                :classes="{ disabled: editMode }"
                :disable="editMode"
                :options="entityOptions"
                label="Entity"
                hint="Which entity is this field relevant for"
              />
            </div>

            <div class="col-xs-6">
              <q-field
                class="q-mb-md"
                helper="E.g. while you create a subscription, you will be required to fill out this field"
                outlined
              >
                <q-checkbox
                  v-model="formData.is_required"
                  class="lm-checkbox lm-among-inputs"
                  color="tertiary"
                  label="Required during creation of entity"
                />
              </q-field>
            </div>
          </div>

          <div class="row q-col-gutter-md q-mb-md">
            <div class="col-xs-6">
              <q-input
                id="name"
                v-model="$v.formData.name.$model"
                :error="$v.formData.name.$error"
                :error-message="$v_displayMessage($v.formData.name)"
                required
                type="text"
                label="Field name"
                name="name"
                hint="Will be used for display in lists and show pages"
                outlined
              />
            </div>

            <div class="col-xs-6">
              <q-field outlined>
                <q-checkbox
                  v-model="formData.copy_to_next_year"
                  class="lm-checkbox lm-among-inputs"
                  color="tertiary"
                  label="Copy data on future subscription periods"
                />
              </q-field>
            </div>
          </div>

          <div class="row q-col-gutter-md q-mb-md">
            <div class="col-xs-6">
              <q-input
                id="description"
                v-model="formData.description"
                type="text"
                label="Description (optional)"
                name="description"
                hint="Helper text, displayed like the current text"
                outlined
              />
            </div>
            <div class="col-xs-6">
              <enhanced-q-select
                id="type"
                v-model="$v.formData.type_id.$model"
                :error="$v.formData.type_id.$error"
                :error-message="$v_displayMessage($v.formData.type_id)"
                :class="{ disabled: editMode }"
                :disable="editMode"
                :options="typeOptions"
                hint="What kind of metadata field is this"
                label="Type"
              />
            </div>

            <div class="col-xs-6">
              <enhanced-q-select
                id="group"
                ref="categories"
                v-model="formData.category_id"
                :options="categoryOptions"
                hint="Used for visual grouping in show pages"
                label="Category (optional)"
                @focus="onCategoriesSelectFocused()"
              />
            </div>

            <div class="col-xs-6 q-pt-sm">
              <LinkButton text="Create new category" @click="openPopupField = true" />
              <PopupField
                :styles="{ left: '-140px', top: '-10px' }"
                :open="openPopupField"
                :loading="creatingCategory"
                position="left-end"
                title="New category"
                @close="openPopupField = false"
                @save="createCategory"
              >
                <template #content>
                  <q-input slot="field" v-model="terms" type="text" class="col" outlined dense />
                </template>
              </PopupField>
            </div>
          </div>

          <!--SELECT-->
          <div v-if="fetchFieldTypeName(formData.type_id) === 'select'" class="row q-col-gutter-md q-mb-md">
            <div class="col-xs-6">
              <div class="row no-margin">
                <div class="col q-if-label text-font-color" style="font-size: 0.75rem; margin-top: 3px">
                  Add dropdown options
                </div>

                <div class="col">
                  <q-btn color="tertiary" class="float-right" flat @click.native="addValueOption()">
                    <i class="fas fa-plus" />
                  </q-btn>
                </div>
              </div>

              <div class="q-mb-md col-xs-12">
                <div
                  v-for="(option, index) of formData.value_options"
                  :key="`value_option_field_${index}`"
                  class="row no-wrap q-col-gutter-xs"
                >
                  <q-input
                    :id="`value_option_field_${index}`"
                    v-model="formData.value_options[index]"
                    :disable="editMode"
                    lazy-rules
                    :rules="[val => (val && val.length > 0) || 'Please type something']"
                    autofocus
                    type="text"
                    style="width: 100%"
                    label="Description"
                    name="description"
                    outlined
                  />

                  <q-btn
                    :disable="editMode"
                    flat
                    color="faded"
                    class="self-start q-mt-sm on-right"
                    @click="deleteValueOption(index)"
                    ><i class="fas fa-trash"
                  /></q-btn>
                </div>

                <template v-if="editMode">
                  <div
                    v-for="(option, index) of newSelectOptions"
                    :key="`new-value_option_field_${index}`"
                    class="row no-wrap q-col-gutter-xs"
                  >
                    <q-input
                      :id="`value_option_field_${index}`"
                      v-model="newSelectOptions[index]"
                      lazy-rules
                      :rules="[val => (val && val.length > 0) || 'Please type something']"
                      autofocus
                      type="text"
                      style="width: 100%"
                      label="Description"
                      name="description"
                      outlined
                    />

                    <q-btn flat color="faded" class="self-start q-mt-sm on-right" @click="deleteValueOption(index)"
                      ><i class="fas fa-trash"
                    /></q-btn>
                  </div>
                </template>
              </div>
            </div>

            <div v-if="formData.value_options && formData.default_values && !editMode" class="col-xs-6">
              <div class="row no-margin">
                <div class="col-xs-12 q-if-label text-font-color" style="font-size: 0.75rem; margin-top: 3px">
                  Set the value(s) for existing entities (optional)
                </div>

                <div
                  v-for="(option, index) of formData.value_options"
                  :key="`default_value_${index}`"
                  class="col-xs-12"
                  style="margin-top: 13px"
                >
                  <q-checkbox
                    v-model="formData.default_values"
                    :label="option"
                    :val="option"
                    color="tertiary"
                    class="lm-checkbox lm-among-inputs"
                    name="description"
                  />
                </div>
              </div>
            </div>
          </div>
          <!--OTHER FIELD TYPES-->
          <div
            v-if="!editMode && ['text', 'textarea', 'checkbox'].includes(fetchFieldTypeName(formData.type_id))"
            class="row q-col-gutter-md"
          >
            <div class="col-xs-6">
              <div class="q-if-label text-font-color" style="font-size: 0.75rem; margin-top: 3px">
                Set the value(s) for existing entities (optional)
              </div>

              <template v-if="fetchFieldTypeName(formData.type_id) === 'text'">
                <q-input v-model="formData.default_values" autofocus outlined />
              </template>

              <template v-if="fetchFieldTypeName(formData.type_id) === 'textarea'">
                <q-input
                  v-model="formData.default_values"
                  style="width: 100%"
                  class="lm-textarea"
                  autofocus
                  rows="3"
                  type="textarea"
                  outlined
                />
              </template>

              <template v-if="fetchFieldTypeName(formData.type_id) === 'checkbox'">
                <CheckboxLikeRadio v-model="formData.default_values" checkbox1-label="Yes" checkbox2-label="No" />
              </template>
            </div>
          </div>
        </form>
      </q-card-section>
    </template>
  </LmDialog>
</template>

<script>
import { LmFormObject } from '@/lib/models/lm-form-object.class'
import { validations } from './validations'
// Libraries
import { cloneDeep, get } from 'lodash'
import { mapState } from 'vuex'
// Components
import EnhancedQSelect from '@components/general/EnhancedQSelect'
import LinkButton from '@/reusable/base-components/LinkButton'
import PopupField from '@/reusable/PopupField/PopupField'
import LmDialog from '@/reusable/base-components/LmDialog/LmDialog'
import CheckboxLikeRadio from '@/reusable/CheckboxLikeRadio'

export default {
  name: 'CreateMetaDataFieldForm',

  components: {
    EnhancedQSelect,
    LinkButton,
    PopupField,
    LmDialog,
    CheckboxLikeRadio
  },

  mixins: [validations],

  props: {
    showing: {
      type: Boolean,
      default: false
    },
    editMode: {
      type: Boolean,
      default: false
    },
    editable: {
      type: [Object, null],
      default: null
    }
  },

  data() {
    return {
      namespace: 'meta-data-fields',
      created: false,
      formData: null,
      creatingCategory: false,
      terms: null,
      unwatchTerms: null,
      unwatchCategories: null,
      style: {
        width: '700px'
      },
      creatingProductType: false,
      openPopupField: false,
      dialogStyles: { minWidth: '900px', minHeight: '650px' },
      newSelectOptions: []
    }
  },

  computed: {
    ...mapState({
      types: state => get(state, 'metaDataFields.supportingData.types', []),
      categories: state => get(state, 'metaDataFields.supportingData.categories', []),
      entities: state => get(state, 'metaDataFields.supportingData.entities', [])
    }),

    modalShow: {
      get() {
        return this.showing
      },
      set(val) {
        !val && this.closeModal()
      }
    },

    typeOptions() {
      if (!this.types) {
        return []
      }
      return this.generateOptions({
        collection: this.types,
        labelKey: 'label',
        valueKey: 'id'
      })
    },

    categoryOptions() {
      if (!this.categories) {
        return []
      }
      return this.generateOptions({ collection: this.categories })
    },

    entityOptions() {
      if (!this.entities) {
        return []
      }
      return this.generateOptions({
        collection: this.entities,
        labelKey: 'label',
        valueKey: 'id'
      })
    },

    hasEmptyOptions() {
      return this.formData && this.formData.value_options.some(val => val.length === 0)
    }
  },

  watch: {
    'formData.type_id'(newVal) {
      if (this.formData && newVal) {
        this.setDefaultValues(newVal)
        if (!this.formData.id) this.$set(this.formData, 'value_options', [])
      }
    }
  },

  mounted() {
    // Prepare form data
    let data = this.editable ? new LmFormObject(cloneDeep(this.editable)) : null
    if (data && !data.default_values) data.default_values = []
    if (data && !data.value_options) data.value_options = []
    this.$set(this, 'formData', data)
  },

  methods: {
    fetchFieldTypeName(typeId) {
      let type = this.types.find(type => {
        return type.id === typeId
      })
      return get(type, 'name', null)
    },

    setDefaultValues(typeId) {
      const type = this.fetchFieldTypeName(typeId)
      if (['text', 'textarea'].includes(type)) this.formData.default_values = ''
      else if (type === 'checkbox') this.formData.default_values = null
      else this.formData.default_values = []
    },

    async createCategory() {
      this.creatingCategory = true
      const response = await this.$store.dispatch('metaDataCategories/create', {
        value: { name: this.terms }
      })
      this.formData.category_id = response.id
      await this.$store.dispatch('metaDataFields/getSupportingData')
      this.creatingCategory = false
      this.terms = null
      this.openPopupField = false
    },

    onCategoriesSelectFocused() {
      this.unwatchTerms = this.$watch(
        () => this.$refs.categories.$refs.select.terms,
        value => {
          if (value) {
            this.terms = value
          }
        }
      )
      this.unwatchCategories = this.$watch(
        () => this.$refs.categories.$refs.select.visibleOptions,
        value => {
          if (!value.length) this.$set(this.formData, 'category_id', null)
        }
      )
    },

    matchesCategory(terms) {
      return !!this.$refs.categories.$refs.select.visibleOptions.find(item => {
        return terms === item.label
      })
    },

    addValueOption() {
      if (this.editMode) {
        this.newSelectOptions.push('')
      } else {
        let opts = cloneDeep(this.formData.value_options)
        opts.push('')
        this.$set(this.formData, 'value_options', opts)
      }
    },

    deleteValueOption(index) {
      if (this.editMode) {
        this.newSelectOptions.splice(index, 1)
      } else {
        let indexOfDefaultValue = this.formData.default_values.findIndex(item => {
          return item === this.formData.value_options[index]
        })
        if (indexOfDefaultValue >= 0) this.formData.default_values.splice(indexOfDefaultValue, 1)
        this.formData.value_options.splice(index, 1)
      }
    },

    prepareFormData() {
      let data = cloneDeep(this.formData)
      delete data.errors
      delete data.originalData

      if (this.editMode && data.type.name === 'select') {
        data.value_options = [...data.value_options, ...this.newSelectOptions]
      }
      return data
    },

    submit() {
      this.$v.$touch()
      if (this.$v.$invalid) return

      let formData = this.prepareFormData()
      this.created = true
      let params = {
        namespace: `meta-data-fields`,
        value: formData
      }
      if (this.editMode) {
        params.id = formData.id
      }
      let action = this.editMode ? 'update' : 'create'
      return this.$store.dispatch(`metaDataFields/${action}`, params).then(result => {
        if (!get(result, 'errors')) {
          this.$emit(action, result)
          this.closeModal()
        }
      })
    },

    closeModal() {
      this.$emit('close')
      this.created = false
      this.terms = null
      if (this.unwatchTerms) this.unwatchTerms()
      if (this.unwatchCategories) this.unwatchCategories()
    }
  }
}
</script>
